"use client";

// Deal drawer — GET /api/admin/deals/{id}. Read-only: the deal snapshot plus
// its immutable bell timeline (audit trail). Deals are edited from the
// salesperson / manager apps, not this dashboard.

import {
  adminApi,
  assetUrl,
  type AdminDealDetail,
  type DealCounterOffer,
  type DealDocument,
  type DealNote,
  type DealWorksheet,
} from "../lib/api";
import { money } from "../lib/format";
import { bellResponse, bellTint, humanize, personName, statusLabel } from "../lib/adminMap";
import { useApi } from "../lib/useApi";
import type { DealStatus } from "../lib/data";
import Modal from "./Modal";
import Icon from "./Icon";
import { Skeleton } from "./Skeleton";
import { PanelEmpty, PanelError } from "./PanelState";

const statusStyle: Record<DealStatus, string> = {
  Active: "var(--accent)",
  "Pending Follow-Up": "var(--warn)",
  "Closed (Sold)": "var(--ok)",
  "Closed (Lost)": "var(--danger)",
};

function stamp(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  // Reporting timezone is UTC (dashboard metadata) — format deterministically.
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 19)} UTC`;
}

/** GET /api/admin/deals/{id}/notes — aggregated status-history + manager notes. */
function Notes({ dealId }: { dealId: number | string }) {
  const { data, loading, error } = useApi<DealNote[]>(
    (signal) => adminApi.dealNotes(dealId, signal),
    [dealId],
    "Failed to load notes.",
  );

  if (loading) return <Skeleton className="h-16 rounded-2xl" />;
  if (error || !data || data.length === 0) return null;

  return (
    <div>
      <p className="text-[13px] font-semibold text-text mb-3">Notes</p>
      <ul className="flex flex-col gap-2">
        {data.map((n) => (
          <li key={`${n.source}-${n.sourceId}`} className="neu-inset rounded-2xl px-4 py-3">
            <p className="text-[13px] text-text">{n.text}</p>
            <p className="text-[11.5px] text-text-faint mt-1">
              {humanize(n.source)} · {personName(n.author)} · {stamp(n.createdAt)}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Sub-resources: GET /api/admin/deals/{id}/{worksheets,counter-offers,documents}.
 *  Each is a separate endpoint, fetched only when the drawer is open. */
function Attachments({ dealId }: { dealId: number }) {
  const worksheets = useApi<DealWorksheet[]>(
    (s) => adminApi.dealWorksheets(dealId, s),
    [dealId],
    "Failed to load worksheets.",
  );
  const offers = useApi<DealCounterOffer[]>(
    (s) => adminApi.dealCounterOffers(dealId, s),
    [dealId],
    "Failed to load counter offers.",
  );
  const documents = useApi<DealDocument[]>(
    (s) => adminApi.dealDocuments(dealId, s),
    [dealId],
    "Failed to load documents.",
  );

  const loading = worksheets.loading || offers.loading || documents.loading;
  if (loading) return <Skeleton className="h-24 rounded-2xl" />;

  const w = worksheets.data ?? [];
  const o = offers.data ?? [];
  const d = documents.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-[13px] font-semibold text-text mb-3">Worksheets · {w.length}</p>
        {w.length === 0 ? (
          <p className="text-[12.5px] text-text-faint">No worksheet submitted.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {w.map((row) => (
              <li key={row.id} className="neu-inset rounded-2xl px-4 py-3 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] text-text">
                    {row.salePrice !== undefined ? money(Number(row.salePrice)) : "—"}
                    {row.monthlyPayment !== undefined && (
                      <span className="text-text-faint"> · {money(Number(row.monthlyPayment))}/mo</span>
                    )}
                  </p>
                  <p className="text-[11.5px] text-text-faint">
                    {personName(row.createdBy)} · {stamp(row.createdAt)}
                  </p>
                </div>
                {row.status && (
                  <span className="text-[12px] font-medium text-text-muted shrink-0">
                    {humanize(row.status)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className="text-[13px] font-semibold text-text mb-3">Counter offers · {o.length}</p>
        {o.length === 0 ? (
          <p className="text-[12.5px] text-text-faint">No counter offers logged.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {o.map((row) => (
              <li key={row.id} className="neu-inset rounded-2xl px-4 py-3 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] text-text">
                    {row.offerPrice !== undefined ? money(Number(row.offerPrice)) : "—"}
                  </p>
                  <p className="text-[11.5px] text-text-faint">
                    {personName(row.createdBy)} · {stamp(row.createdAt)}
                  </p>
                </div>
                {row.status && (
                  <span className="text-[12px] font-medium text-text-muted shrink-0">
                    {humanize(row.status)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className="text-[13px] font-semibold text-text mb-3">Documents · {d.length}</p>
        {d.length === 0 ? (
          <p className="text-[12.5px] text-text-faint">No documents uploaded.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {d.map((row) => {
              const href = assetUrl(row.url ?? row.filePath);
              return (
                <li key={row.id} className="neu-inset rounded-2xl px-4 py-3 flex items-center gap-3">
                  <Icon name="download" size={16} className="text-text-faint shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] text-text truncate">
                      {row.name ?? row.fileName ?? `Document #${row.id}`}
                    </p>
                    <p className="text-[11.5px] text-text-faint">{stamp(row.createdAt)}</p>
                  </div>
                  {href && (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="neu-btn h-9 px-3 rounded-xl text-[12px] font-medium text-text-muted shrink-0"
                    >
                      Open
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function Body({ detail }: { detail: AdminDealDetail }) {
  const { deal, customer, salesperson, manager, bellTimeline } = detail;
  const label = statusLabel(deal.status);

  // The API sends gross as a decimal string and omits it until it is entered.
  // An unparseable or absent value drops the tile rather than showing "$0",
  // which would read as a closed deal with no gross.
  const gross = Number(deal.grossProfit);
  const hasGross =
    deal.grossProfit !== null && deal.grossProfit !== undefined && Number.isFinite(gross);

  const stats = [
    // Gross leads: it is the deal's headline business outcome.
    ...(hasGross ? [{ k: "Gross Profit", v: money(gross), tone: "var(--ok)" }] : []),
    { k: "Bell Count", v: String(deal.bellCount ?? 0) },
    { k: "Stage", v: humanize(deal.currentStage) },
    { k: "Salesperson", v: personName(salesperson) },
    { k: "Manager", v: personName(manager) },
    { k: "Priority", v: humanize(deal.priority) },
    { k: "Checked In", v: stamp(deal.checkInAt) },
  ];

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div>
        <p className="font-display text-2xl text-text">{customer?.name || "Guest Customer"}</p>
        <p className="text-[13px] text-text-faint">
          #{deal.code} · {deal.vehicle || "Vehicle not set"}
        </p>
        <div className="flex items-center gap-3 mt-2">
          <span
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium"
            style={{ color: statusStyle[label] }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusStyle[label] }} />
            {label}
          </span>
          {deal.isCustomerLeaving && (
            <span className="text-[12.5px] font-semibold" style={{ color: "var(--danger)" }}>
              Customer leaving
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        {stats.map((s) => (
          <div key={s.k} className="neu-inset rounded-2xl px-4 py-3">
            <p className="text-[11.5px] text-text-faint">{s.k}</p>
            <p
              className="font-display text-lg mt-0.5 truncate"
              style={{ color: s.tone ?? "var(--text)" }}
            >
              {s.v}
            </p>
          </div>
        ))}
      </div>

      {(deal.note || deal.managerNote) && (
        <div className="flex flex-col gap-2">
          {deal.note && (
            <div className="neu-inset rounded-2xl px-4 py-3">
              <p className="text-[11.5px] text-text-faint">Note</p>
              <p className="text-[13px] text-text mt-0.5">{deal.note}</p>
            </div>
          )}
          {deal.managerNote && (
            <div className="neu-inset rounded-2xl px-4 py-3">
              <p className="text-[11.5px] text-text-faint">Manager note</p>
              <p className="text-[13px] text-text mt-0.5">{deal.managerNote}</p>
            </div>
          )}
        </div>
      )}

      <Notes dealId={deal.id} />

      {/* Bell timeline (immutable event stream) */}
      <div>
        <p className="text-[13px] font-semibold text-text mb-3">Bell Timeline</p>
        {bellTimeline.length === 0 ? (
          <p className="text-[12.5px] text-text-faint">No bell events recorded.</p>
        ) : (
          <ol className="flex flex-col">
            {bellTimeline.map((b, i) => (
              <li key={b.id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    className="w-2.5 h-2.5 rounded-full mt-1.5"
                    style={{ background: bellTint(b.type).color }}
                  />
                  {i < bellTimeline.length - 1 && (
                    <span className="w-px flex-1 my-1" style={{ background: "var(--neu-dark)" }} />
                  )}
                </div>
                <div className="pb-4 min-w-0">
                  <p className="text-[13.5px] text-text">{humanize(b.type)}</p>
                  <p className="text-[11.5px] text-text-faint">
                    {stamp(b.createdAt)} · {personName(b.triggeredBy)} · {bellResponse(b)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <Attachments dealId={deal.id} />
    </div>
  );
}

export default function DealDetail({
  dealId,
  onClose,
}: {
  dealId: number | string | null;
  onClose: () => void;
}) {
  const { data, loading, error, reload } = useApi<AdminDealDetail | null>(
    (signal) => (dealId === null ? Promise.resolve(null) : adminApi.deal(dealId, signal)),
    [dealId],
    "Failed to load the deal.",
  );

  return (
    <Modal open={dealId !== null} onClose={onClose} title="Deal Detail" icon="handshake" size="lg">
      {loading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-2xl" />
            ))}
          </div>
          <Skeleton className="h-40 rounded-2xl" />
        </div>
      ) : error ? (
        <PanelError message={error} onRetry={reload} />
      ) : data ? (
        <Body detail={data} />
      ) : (
        <PanelEmpty title="No deal selected" />
      )}
    </Modal>
  );
}
